/**
 * Creates a debounced version of a function that delays invoking the function
 * until after `delay` milliseconds have passed since the last time the
 * debounced function was invoked.
 *
 * @template T The type of the function to debounce.
 * @param func The function to debounce.
 * @param delay The number of milliseconds to delay.
 * @returns The new debounced function.
 */
export function Debounce<T extends (...args: any[]) => any>(func: T, delay: number): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  // Return a new function that will be called instead of the original
  return function(this: ThisParameterType<T>, ...args: Parameters<T>) {
    // Capture the 'this' context and arguments for the original function
    const context = this;

    // If there is already a scheduled call, cancel it
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Schedule a new call to the original function after 'delay' ms
    timeoutId = setTimeout(() => {
      func.apply(context, args); // Call the original function with the correct 'this' and arguments
      timeoutId = null; // Clear the timer ID after execution (optional but good practice)
    }, delay);
  };
}